import React, { useState } from 'react';
import {
  Monitor, Laptop, Cpu, Server, ScreenShare, BadgeCheck,
  Wrench, ArrowRight, ExternalLink, ShieldCheck, Phone, Star,
} from 'lucide-react';

// ── Identidade visual Daten ──────────────────────────────────────────
// Cor primária: #1d3557 (navy Daten) · Acento: #457b9d

const DATEN_NAVY  = '#1d3557';
const DATEN_BLUE  = '#457b9d';

const PRODUCTS = [
  {
    id: 'notebooks',
    title: 'Notebooks Daten',
    subtitle: 'Mobilidade e Performance',
    icon: Laptop,
    image: 'https://www.daten.com.br/novo_site/imagens/ads/35c479ee2b3dc1a7acca50cbd5e83f600524c479ee2b3dc1a7acca50cbd5e83f6005note.png',
    description: 'Notebooks Daten com processadores Intel e AMD de última geração, display Full HD e bateria de longa duração. Linha para empresas, setor público e uso pessoal — homologados para licitações.',
    features: [
      'Processadores Intel Core / AMD Ryzen',
      'Display Full HD antirreflexo',
      'Bateria de longa duração (até 8h)',
      'Teclado retroiluminado ABNT2',
      'Garantia e suporte Daten autorizado',
      'Disponível em licitações públicas',
    ],
    cta: 'https://www.daten.com.br/setor-publico.php?c=11#11a',
  },
  {
    id: 'desktops',
    title: 'Desktops Daten',
    subtitle: 'Robustez Corporativa',
    icon: Monitor,
    image: 'https://www.daten.com.br/files/46c479ee2b3dc1a7acca50cbd5e83f600537c479ee2b3dc1a7acca50cbd5e83f6005desk1_gov.png',
    description: 'Computadores desktop Daten para uso corporativo e governamental. Alta durabilidade, peças sobressalentes garantidas e configurações customizáveis de acordo com o projeto.',
    features: [
      'Gabinetes compactos ou torre',
      'Configurações sob demanda',
      'Homologados para setor público',
      'Garantia de 12 meses de fábrica',
      'Suporte técnico autorizado local',
      'Reposição de peças garantida',
    ],
    cta: 'https://www.daten.com.br/setor-publico.php?c=6#6a',
  },
  {
    id: 'allinone',
    title: 'All-in-One Daten',
    subtitle: 'Tudo em Um Só Equipamento',
    icon: ScreenShare,
    image: 'http://www.daten.com.br/novo_site/imagens/ads/48c479ee2b3dc1a7acca50cbd5e83f600543c479ee2b3dc1a7acca50cbd5e83f6005aio.png',
    description: 'Computadores All-in-One Daten com design elegante: CPU integrada ao monitor, sem cabos soltos e perfeitos para recepções, clínicas, escritórios e PDVs.',
    features: [
      'Design slim — CPU integrada ao monitor',
      'Tela touch opcional',
      'Ideal para balcões e PDVs',
      'Configurações de 21" a 27"',
      'Baixo consumo de energia',
      'Garantia e assistência local',
    ],
    cta: 'https://www.daten.com.br/setor-publico.php?c=3#3a',
  },
  {
    id: 'minipc',
    title: 'Mini PCs Daten',
    subtitle: 'Compacto e Poderoso',
    icon: Cpu,
    image: 'http://www.daten.com.br/novo_site/imagens/ads/52c479ee2b3dc1a7acca50cbd5e83f6005mini.png',
    description: 'Mini PCs Daten com tamanho reduzido, baixo ruído e alto desempenho. Ideais para instalações discretas em salas de reunião, quiosques e ambientes que exigem hardware compacto.',
    features: [
      'Formato ultra-compacto',
      'Silencioso e de baixo consumo',
      'Múltiplas saídas de vídeo (HDMI + DP)',
      'Ideal para salas de reunião e quiosques',
      'Fácil montagem em monitor (VESA)',
      'Garantia Daten com suporte local',
    ],
    cta: 'https://www.daten.com.br/setor-publico.php?c=7#7a',
  },
  {
    id: 'servidores',
    title: 'Servidores Daten',
    subtitle: 'Infraestrutura Confiável',
    icon: Server,
    image: 'https://www.daten.com.br/files/15c479ee2b3dc1a7acca50cbd5e83f6005Sem-Titulo-1.png',
    description: 'Servidores Daten para pequenas e médias empresas: alta disponibilidade, suporte a virtualização, redundância de energia e SLAs de garantia adequados para ambientes críticos.',
    features: [
      'Tower e Rack',
      'Suporte a virtualização (VMware, Hyper-V)',
      'Redundância de fonte e RAID nativo',
      'Adequados para ERP e sistemas corporativos',
      'Garantia de fábrica e suporte autorizado',
      'Configurações customizadas',
    ],
    cta: 'https://www.daten.com.br/setor-publico.php?c=14#14a',
  },
  {
    id: 'monitores',
    title: 'Monitores Daten',
    subtitle: 'Clareza e Produtividade',
    icon: Monitor,
    image: 'https://www.daten.com.br/files/55c479ee2b3dc1a7acca50cbd5e83f6005monitor_2.png',
    description: 'Monitores Daten Full HD e QHD com painel IPS de alta fidelidade de cores. Ideais para estações de trabalho, salas de controle, projeto gráfico e atendimento.',
    features: [
      'Painel IPS / VA Full HD',
      'Sem flicker, filtro de luz azul',
      'Suporte VESA universal',
      'Entrada HDMI + VGA + DisplayPort',
      'Até 27" com ajuste de altura e inclinação',
      'Garantia e assistência autorizada',
    ],
    cta: 'https://www.daten.com.br/setor-publico.php?c=15#15a',
  },
];

const Daten = () => {
  const [activeTab, setActiveTab] = useState('notebooks');
  const active  = PRODUCTS.find(p => p.id === activeTab);
  const ActiveIcon = active.icon;

  const whatsappUrl = `https://wa.me/5516991378312?text=${encodeURIComponent(
    'Olá! Gostaria de informações sobre produtos e assistência técnica Daten.'
  )}`;

  return (
    <section
      id="daten"
      className="py-20 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0d1b2a 0%, #1d3557 40%, #0d1b2a 100%)' }}
      aria-label="Fernando Tech — Assistência Autorizada e Revenda Oficial Daten"
      itemScope
      itemType="https://schema.org/LocalBusiness"
    >
      {/* Schema.org invisible */}
      <meta itemProp="name" content="Fernando Tech — Assistência Autorizada Daten" />
      <meta itemProp="description" content="Assistência Técnica Autorizada e Revenda Oficial Daten em Ribeirão Preto/SP. Atendemos notebooks, desktops, all-in-one, mini PCs, servidores e monitores Daten." />
      <link itemProp="url" href="https://fernandotech.com.br/#daten" />
      <div itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
        <meta itemProp="streetAddress" content="Rua Major Leopoldo Jose Leonardeli, 100" />
        <meta itemProp="addressLocality" content="Ribeirão Preto" />
        <meta itemProp="addressRegion" content="SP" />
        <meta itemProp="addressCountry" content="BR" />
      </div>

      {/* Background glow */}
      <div className="absolute inset-0 opacity-15 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full filter blur-3xl" style={{ background: DATEN_BLUE }} />
        <div className="absolute bottom-0 right-1/4 w-72 h-72 rounded-full filter blur-3xl" style={{ background: DATEN_NAVY }} />
      </div>

      <div className="container mx-auto px-4 relative z-10">

        {/* ── HEADER ─────────────────────────────────────────────── */}
        <header className="text-center mb-12">
          {/* Badges de parceria */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-sm font-bold px-4 py-1.5 rounded-full backdrop-blur-sm">
              <BadgeCheck className="w-4 h-4 text-green-400" /> Assistência Técnica Autorizada
            </span>
            <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white text-sm font-bold px-4 py-1.5 rounded-full backdrop-blur-sm">
              <Star className="w-4 h-4 text-yellow-400" /> Revenda Oficial
            </span>
          </div>

          {/* Logo Daten estilizado */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <div
              className="text-5xl md:text-6xl font-black tracking-widest select-none"
              style={{ color: '#fff', fontStyle: 'italic', textShadow: '0 0 40px rgba(69,123,157,0.6)' }}
              aria-label="Daten"
            >
              DATEN
            </div>
          </div>

          <h2 className="text-xl md:text-2xl font-semibold text-white mb-3">
            Autorizada e Revenda Oficial em{' '}
            <span className="text-blue-300 font-bold">Ribeirão Preto / SP</span>
          </h2>

          <p className="text-blue-200 max-w-2xl mx-auto text-base mb-6">
            A <strong>Fernando Tech</strong> é uma <strong>Assistência Técnica Autorizada Daten</strong> em Ribeirão Preto,
            com atendimento em um raio de até <strong>200 km</strong> na região. Realizamos manutenções com garantia de fábrica
            e também comercializamos toda a linha de computadores, notebooks, servidores e monitores Daten para
            pessoas físicas, empresas e órgãos públicos.
          </p>

          {/* Verificação oficial + CTAs */}
          <div className="flex flex-wrap justify-center gap-3 mb-4">
            <a
              href="https://www.daten.com.br/assistencia-autorizada.php"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#1d3557] font-bold px-5 py-2.5 rounded-xl text-sm hover:bg-blue-50 transition-all shadow-lg"
              data-testid="daten-verificar-btn"
            >
              <ShieldCheck className="w-4 h-4" />
              Verificar no Site Oficial Daten
              <ExternalLink className="w-3 h-3 opacity-60" />
            </a>
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-all shadow-lg"
              style={{ background: '#25D366' }}
              data-testid="daten-whatsapp-btn"
            >
              <Phone className="w-4 h-4" /> Solicitar Orçamento ou Serviço
            </a>
          </div>

          {/* espaçamento */}
        </header>

        {/* ── TABS DE PRODUTOS ────────────────────────────────────── */}
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-blue-200 text-sm font-medium mb-5 uppercase tracking-widest">
            Produtos que Vendemos e Assistimos
          </p>

          {/* Tab buttons */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {PRODUCTS.map(p => {
              const Icon = p.icon;
              const isActive = activeTab === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setActiveTab(p.id)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200"
                  style={isActive
                    ? { background: DATEN_BLUE, color: '#fff', boxShadow: `0 4px 20px ${DATEN_BLUE}60` }
                    : { background: 'rgba(255,255,255,0.06)', color: '#93c5fd', border: '1px solid rgba(255,255,255,0.1)' }
                  }
                  aria-pressed={isActive}
                >
                  <Icon className="w-4 h-4" />
                  {p.title.replace(' Daten', '')}
                </button>
              );
            })}
          </div>

          {/* Active product card */}
          <div
            className="rounded-2xl overflow-hidden border"
            style={{ borderColor: `${DATEN_BLUE}40`, background: 'rgba(29,53,87,0.5)', backdropFilter: 'blur(12px)' }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              {/* Image side */}
              <div
                className="flex items-center justify-center p-8 md:p-12"
                style={{ background: `linear-gradient(135deg, ${DATEN_NAVY} 0%, #0d1b2a 100%)` }}
              >
                <div className="text-center">
                  <img
                    src={active.image}
                    alt={`${active.title} — Assistência Autorizada Fernando Tech Ribeirão Preto`}
                    className="max-h-52 w-auto object-contain mx-auto mb-4 drop-shadow-2xl"
                    loading="lazy"
                    onError={e => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  {/* Fallback icon */}
                  <div className="hidden items-center justify-center w-40 h-40 mx-auto rounded-2xl"
                    style={{ background: `${DATEN_BLUE}20`, border: `1px solid ${DATEN_BLUE}40` }}>
                    <ActiveIcon className="w-20 h-20 opacity-40" style={{ color: DATEN_BLUE }} />
                  </div>
                  <span
                    className="inline-block text-sm font-bold px-4 py-1 rounded-full mt-2"
                    style={{ background: `${DATEN_BLUE}30`, color: '#93c5fd', border: `1px solid ${DATEN_BLUE}50` }}
                  >
                    {active.subtitle}
                  </span>
                </div>
              </div>

              {/* Content side */}
              <div className="p-8">
                <h3
                  className="text-2xl font-bold mb-1"
                  style={{ color: '#e2e8f0' }}
                  itemProp="name"
                >
                  {active.title}
                </h3>
                <p className="text-blue-300 text-sm mb-4" itemProp="description">
                  {active.description}
                </p>

                <ul className="space-y-2 mb-6">
                  {active.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-blue-100">
                      <BadgeCheck className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#4ade80' }} />
                      {f}
                    </li>
                  ))}
                </ul>

                <div className="flex flex-wrap gap-3">
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-bold px-5 py-2.5 rounded-xl text-sm text-white transition-all hover:opacity-90"
                    style={{ background: '#25D366' }}
                    data-testid={`daten-cta-${active.id}`}
                  >
                    <Wrench className="w-4 h-4" /> Solicitar Serviço / Comprar
                  </a>
                  <a
                    href={active.cta}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-semibold px-5 py-2.5 rounded-xl text-sm transition-all hover:opacity-80"
                    style={{ background: 'rgba(255,255,255,0.08)', color: '#93c5fd', border: `1px solid ${DATEN_BLUE}60` }}
                  >
                    Ver no Site Daten <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* ── Por que a Fernando Tech ─────────────────────────── */}
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                icon: ShieldCheck,
                title: 'Garantia de Fábrica',
                text: 'Todos os reparos realizados mantêm a garantia oficial Daten, com peças originais e técnicos homologados.',
              },
              {
                icon: BadgeCheck,
                title: 'Autorizado Oficialmente',
                text: 'Fernando Tech está cadastrada e listada no portal oficial daten.com.br como Assistência Autorizada SP.',
              },
              {
                icon: Wrench,
                title: 'Atendimento Local',
                text: 'Ribeirão Preto e região. Sem envio para outro estado: diagnóstico, reparo e entrega feitos aqui.',
              },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div
                  key={i}
                  className="rounded-2xl p-5 text-center border"
                  style={{ background: 'rgba(255,255,255,0.04)', borderColor: `${DATEN_BLUE}30` }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{ background: `${DATEN_BLUE}25` }}
                  >
                    <Icon className="w-6 h-6" style={{ color: '#93c5fd' }} />
                  </div>
                  <h4 className="text-white font-bold mb-2 text-sm">{item.title}</h4>
                  <p className="text-blue-200/70 text-xs leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>

          {/* ── Rodapé da seção ─────────────────────────────────── */}
          <p className="text-center text-blue-300/40 text-xs mt-8">
            Daten Tecnologia Ltda é marca registrada de seus respectivos proprietários.
            Fernando Tech atua como revendedor e assistência autorizada independente.
            Para garantia e suporte oficial, acesse{' '}
            <a
              href="https://www.daten.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-blue-300/70"
            >
              www.daten.com.br
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default Daten;
